import {inject, Injectable} from '@angular/core';
import {LocalAnalysis} from "../../core/models/local-analysis";
import {filter, firstValueFrom, from, map, Observable, of, switchMap, tap, throwError} from "rxjs";
import {Analysis} from "../../core/models/analysis";
import {CustomError} from "../../core/models/error";
import {PictureStatus} from "../../core/models/picture";
import {AnalysisStatusEnum} from "../../core/models/analysis-status";
import {AnalysisStage} from "../../core/models/analysis-stage";
import {ReferenceApiService} from "../../core/api/reference-api/reference-api.service";
import {PictureUploadService} from "./picture-upload.service";
import {IndexedDBService} from "./indexed-db.service";
import {Product} from "../../core/models/product";
import {VehicleIdentificationScenarioType} from "../../core/models/vehicle-identification-scenario";
import {AppStateService} from "../../core/state/app-state.service";
import {AnalysisCreationService} from "./analysis-creation.service";
import {PhotoshootService} from "../../features/shared/photoshoot/services/photoshoot.service";
import {PostAnalysisResponseDto} from "../../core/dto/post-analysis-response-dto";
import {Vehicle} from "../../core/models/vehicle";
import {ExternalApiService} from "../../core/api/external-api/external-api.service";
import {FinishAnalysisRequestDto} from "../../core/dto/finish-analysis-request-dto";
import {FinishAnalysisResponseDto} from "../../core/dto/finish-analysis-response-dto";
import {AnalysisTimingService} from "./analysis-timing.service";

@Injectable({
  providedIn: 'root'
})
export class AnalysisService {

  protected referenceApi = inject(ReferenceApiService);
  protected externalApi = inject(ExternalApiService);
  protected uploadService = inject(PictureUploadService);
  protected indexedDB = inject(IndexedDBService);
  protected appStateService = inject(AppStateService);
  protected analysisCreation = inject(AnalysisCreationService);
  protected photoshootService = inject(PhotoshootService);
  protected analysisTimingService = inject(AnalysisTimingService);

  getOrCreateAnalysis(): Observable<Analysis> {
    const analysis = this.appStateService.analysis();
    if (analysis?.id) {
      return of(analysis);
    }
    return from(this.createAnalysisAndVehicle()).pipe(
      map(() => this.appStateService.analysis()!)
    );
  }

  abortAnalysis() {
    const analysisId = this.appStateService.analysis()?.id;

    if (!analysisId) {
      return throwError(() => new Error(CustomError.NO_ANALYSIS));
    }

    // TODO(TAP-5508): no /v1/web-app endpoint to abort an analysis yet, still uses the reference API.
    return this.referenceApi.updateAnalysis(analysisId, AnalysisStatusEnum.ABORTED).pipe(
      tap(() => this.uploadService.cancelUpload()),
      switchMap(() => {
        if (this.appStateService.product() === Product.COLLECT || this.needsNewIdentificationPicture()) {
          return from(this.appStateService.clear()).pipe(map(() => null));
        }
        return from(this.appStateService.clearLocalPicturesAndAnalysis()).pipe(
          switchMap(() => from(this.createAnalysisAndVehicle()))
        );
      })
    );
  }

  private needsNewIdentificationPicture() {
    const scenario = this.appStateService.identificationScenario();
    return scenario === VehicleIdentificationScenarioType.VIN
      || scenario === VehicleIdentificationScenarioType.REGISTRATION_CARD_PICTURE;
  }

  /**
   * Ensures an analysis exists for the current vehicle.
   * POST /v1/web-app/analyses is idempotent per vehicle: it returns the ongoing
   * analysis when one exists.
   */
  getAnalysis() {
    const vehicle = this.appStateService.vehicle();
    if (!vehicle?.id && !vehicle?.license) {
      return throwError(() => new Error(CustomError.NO_SUCH_VEHICLE));
    }

    return from(this.createAnalysisAndVehicle()).pipe(
      switchMap((response) => {
        if (response.status === AnalysisStatusEnum.UPLOADED) {
          return throwError(() => new Error(CustomError.ONGOING_ANALYSIS));
        }
        return of(this.appStateService.analysis()!);
      })
    );
  }

  // One common point to finish the analysis and abstract the timings
  finishAnalysis(dto: FinishAnalysisRequestDto = {}): Observable<FinishAnalysisResponseDto> {
    const analysisId = this.appStateService.analysis()?.id;
    if (!analysisId) {
      return throwError(() => new Error(CustomError.NO_ANALYSIS));
    }
    return this.externalApi.finishAnalysis(analysisId, {
      ...dto,
      ...this.analysisTimingService.getTimings(analysisId)
    });
  }

  private handleLocalAnalysis(analysis: Analysis, localAnalysis?: LocalAnalysis) {
    if (localAnalysis) {
      return from(this.createSpareTyre(analysis, localAnalysis)).pipe(
        switchMap(() => this.indexedDB.getAllTyresPictures()),
        map((pictures) => {
          this.indexedDB.changePicturesStatus(PictureStatus.UPLOADING);
          pictures.forEach((picture) => {
            picture.status = PictureStatus.UPLOADING;
            this.photoshootService.setPicture(picture);
            this.uploadService.uploadPicture(picture);
          });
          return analysis;
        })
      );
    }
    return of(analysis);
  }

  private createSpareTyre(analysis: Analysis, localAnalysis: LocalAnalysis) {
    if (analysis.id && localAnalysis.spareTyreResult?.spareTyreResult) {
      // TODO(TAP-5508): no /v1/web-app endpoint for spare tyre yet, still uses the reference API.
      return this.referenceApi.createSpareTyreAnalysis$(analysis.id).pipe(
        map((response) => response.id),
        filter((vehicleAnalysisId) => !!vehicleAnalysisId),
        switchMap((vehicleAnalysisId) => this.referenceApi.updateSpareTyreAnalysis$(vehicleAnalysisId, localAnalysis.spareTyreResult!.spareTyreResult!).pipe(
          map(() => vehicleAnalysisId))
        ),
        switchMap((vehicleAnalysisId) => this.referenceApi.updateVehicleAnalysis$(vehicleAnalysisId, AnalysisStatusEnum.UPLOADED).pipe(
          map(() => vehicleAnalysisId))
        ),
        tap((vehicleAnalysisId) =>
          // @ts-ignore
          this.appStateService.patch( {analysis: {
            ...this.appStateService.analysis(),
            spareTyreResult: {
              vehicleAnalysisId,
              spareTyreResult: localAnalysis.spareTyreResult!.spareTyreResult!,
              stage: AnalysisStage.TERMINAL
            }
          }}))
      );
    } else {
      return of(undefined);
    }
  }

  /**
   * Creates the analysis, or resumes the ongoing one, for the current product.
   *
   * `localAnalysis` is only provided when restarting an aborted analysis (analysis.resolver).
   * Despite its type, the caller passes the stored `Analysis` of the *current* product: the
   * context fields below (vehicle, product, company...) are therefore undefined in practice and
   * `createOrResume$` ignores undefined overrides, so the whole context comes from the current
   * state. They are still forwarded so that a real LocalAnalysis, if one is ever passed, wins.
   *
   * There is one stored analysis per product (storage keys are prefixed with it), and a product
   * switch reloads the matching state: an analysis from another product cannot reach this method.
   */
  async createAnalysisAndVehicle(localAnalysis?: LocalAnalysis) {
    const {vehicle, product, company, fleet, postcode, externalId} = localAnalysis || {};

    if ((company ?? this.appStateService.company())?.key) {
      const previousAnalysisId = this.appStateService.analysis()?.id;
      const analysisResponse = await firstValueFrom(
        this.analysisCreation.createOrResume$(this.appStateService.snapshot, {vehicle, product, company, fleet, postcode, externalId}));
      const analysis = this.handleAnalysisCreation(analysisResponse);
      if (localAnalysis) {
        // Local resume: the stored pictures are re-uploaded, do not purge them.
        await firstValueFrom(this.handleLocalAnalysis(analysis, localAnalysis));
      } else {
        await this.photoshootService.restoreFromAnalysis(analysis, previousAnalysisId);
      }
      return analysisResponse;
    } else {
      return Promise.reject(new Error(CustomError.NO_SUCH_COMPANY))
    }
  }

  private handleAnalysisCreation(analysisResponse: PostAnalysisResponseDto) {
    const analysis = Analysis.create(analysisResponse);
    const previousId = this.appStateService.analysis()?.id;
    if (previousId && previousId !== analysis.id) {
      this.photoshootService.clearPictures();
    }
    const vehicle = Vehicle.create(analysisResponse.vehicle.id, analysisResponse.vehicle.license, analysisResponse.vehicle.licenseCountry);
    this.appStateService.patch({analysis, vehicle});
    return analysis;
  }
}
